import React, { useCallback, useMemo, useState } from "react";
import { Alert, PlainButton } from "@jugl-web/ui-kit";
import { cx, useClipboard, useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import { ReactComponent as Email2Icon } from "./assets/email-2.svg";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as WorldIcon } from "./assets/world.svg";
import { ReactComponent as GlobeIcon } from "./assets/globe.svg";
import { ReactComponent as StarIcon } from "./assets/star.svg";
import { ReactComponent as ShuffleIcon } from "./assets/shuffle.svg";
import { ReactComponent as InboxIcon } from "./assets/inbox.svg";
import { ReactComponent as FeatherIcon } from "./assets/feather.svg";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";
import { collapsedIconButtonClassName } from "../../../../consts";

export const EmailAccountCard: React.FC<{
  isClickable?: boolean;
  variant?: "card" | "tile";
}> = ({ isClickable, variant = "card" }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();
  const { copyToClipboard } = useClipboard();

  const {
    emailAccount,
    isConnected,
    disconnectEmailAccount,
    isDisconnectingEmailAccount,
  } = useEmailAccount();

  const isGmailConnected = emailAccount?.type === "gmail";
  const isEmailForwardingVerified =
    !!emailAccount?.metadata?.email_forwarding_verified;
  const status =
    isGmailConnected || isEmailForwardingVerified
      ? "connected"
      : isConnected
        ? "verify"
        : undefined;

  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);

  const handleDisconnectAccount = useCallback(async () => {
    try {
      await disconnectEmailAccount({
        successMessage: t({
          id: "client-support-page.email-was-disconnected",
          defaultMessage: "Email was disconnected",
        }),
      });
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
    } catch {
      // Error UI handled via meta + apiResultMiddleware
    }
  }, [
    disconnectEmailAccount,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
  ]);

  const menuItems = useMemo(
    () =>
      isGmailConnected
        ? [
            [
              {
                id: "disconnect-account",
                label: t({
                  id: "client-support-page.disconnect-account",
                  defaultMessage: "Disconnect Account",
                }),
                icon: <RemoveIcon />,
                onSelect: () => setIsDisconnectAlertOpen(true),
                isDisabled: isDisconnectingEmailAccount,
              },
            ],
          ]
        : undefined,
    [isDisconnectingEmailAccount, isGmailConnected, t]
  );

  const disconnectAlert = (
    <Alert
      isOpen={isDisconnectAlertOpen}
      onRequestClose={() => setIsDisconnectAlertOpen(false)}
      title={t({
        id: "client-support-page.disconnect-account",
        defaultMessage: "Disconnect Account",
      })}
      content={t(
        {
          id: "client-support-page.disconnect-email-account",
          defaultMessage:
            "Are you sure you want to disconnect {email} Email account?",
        },
        {
          email: (
            <span className="font-medium text-primary">
              {emailAccount?.email}
            </span>
          ),
        }
      )}
      buttons={[
        {
          text: t({
            id: "common.cancel",
            defaultMessage: "Cancel",
          }),
          color: "grey",
          role: "close",
        },
        {
          text: t({
            id: "client-support-page.disconnect",
            defaultMessage: "Disconnect",
          }),
          onClick: async (_, { closeAlert }) => {
            closeAlert();
            await handleDisconnectAccount();
          },
        },
      ]}
    />
  );

  if (variant === "tile") {
    const handleClick = () => {
      if (!isClickable) {
        return;
      }

      transitionTo({
        name: isConnected ? "emailAccount" : "emailSetup",
      });
    };

    return (
      <CRMAccountTile
        isClickable={isClickable}
        title={t({
          id: "client-support-page.email",
          defaultMessage: "Email",
        })}
        isConnected={isConnected}
        onClick={handleClick}
        icon={<Email2Icon />}
        status={status}
        features={
          isConnected
            ? [
                { icon: <GlobeIcon />, label: emailAccount?.email ?? "" },
                ...(isEmailForwardingVerified &&
                emailAccount?.metadata?.client_email
                  ? [
                      {
                        icon: <GlobeIcon />,
                        label: emailAccount.metadata.client_email,
                      },
                    ]
                  : []),
              ]
            : [
                {
                  icon: <InboxIcon />,
                  label: t({
                    id: "client-support-page.email-tile-feature-shared-mailbox",
                    defaultMessage: "Shared mailbox",
                  }),
                },
                {
                  icon: <ShuffleIcon />,
                  label: t({
                    id: "client-support-page.email-tile-feature-forwarding",
                    defaultMessage: "Email forwarding",
                  }),
                },
                {
                  icon: <StarIcon />,
                  label: t({
                    id: "client-support-page.email-tile-feature-ai-instructions",
                    defaultMessage: "Custom AI instructions",
                  }),
                },
                {
                  icon: <FeatherIcon />,
                  label: t({
                    id: "client-support-page.email-tile-feature-ai-signature",
                    defaultMessage: "Custom AI signature",
                  }),
                },
              ]
        }
      />
    );
  }

  if (!emailAccount) return null;

  return (
    <>
      <CRMAccountCard
        icon={EmailIcon}
        title={t({ id: "client-support-page.email", defaultMessage: "Email" })}
        description={
          isGmailConnected
            ? emailAccount.email
            : isEmailForwardingVerified
              ? t({
                  id: "client-support-page.live",
                  defaultMessage: "Live",
                })
              : t({
                  id: "client-support-page.set-email-forwarding-to-use-inbox",
                  defaultMessage:
                    "Set email forwarding to use your email inbox",
                })
        }
        status={status}
        onClick={
          isClickable ? () => transitionTo({ name: "emailAccount" }) : undefined
        }
        menuItems={menuItems}
      >
        {!isGmailConnected && (
          <div className="flex w-full max-w-125 flex-col gap-4">
            <span className="font-secondary text-sm tracking-[0.14px] text-dark-800">
              {t({
                id: "client-support-page.email-forwarding-address",
                defaultMessage: "Email forwarding address:",
              })}
            </span>
            <div className="flex h-16 items-center gap-2.5 rounded-2xl border border-solid border-dark-200 bg-white px-4">
              <WorldIcon />
              <span className="grow truncate font-secondary leading-[140%] font-medium tracking-[0.16px] text-dark">
                {emailAccount.email}
              </span>
              <PlainButton
                className={cx("h-8 w-8", collapsedIconButtonClassName)}
                onClick={() => copyToClipboard(emailAccount.email)}
              >
                <CopyIcon />
              </PlainButton>
            </div>
          </div>
        )}
      </CRMAccountCard>
      {isGmailConnected && disconnectAlert}
    </>
  );
};
